import React, { useState, useCallback } from 'react'
import {
    PrimaryButton,
    TextInput,
    Textarea,
    EmailInput,
    Dropdown,
    RadioButtonGroup
} from '@posten/hedwig-react'
import CheckboxGroup from './CheckboxGroup'
import FileInput from './FileInput'
import { validateFormInput } from '../../../lib/validation/form-validation'

const KPForm = ({
    form,
    recaptchaSiteKey,
    recaptchaIsConfigured,
    errorMessages
}) => {
    const [inputs, setInputs] = useState(
        form.inputs.map((input) => ({ ...input, value: input.value }))
    )

    const [formSubmitted, setFormSubmitted] = useState()
    const [successMessage, setSuccessMessage] = useState()
    const [serverErrorMessage, setServerErrorMessage] = useState()

    const handleSubmitResponse = useCallback(async (response) => {
        const responseMessage = await response.json().then((res) => res.value)
        if (response?.status >= 400) {
            setServerErrorMessage(responseMessage ?? 'Error')
        } else {
            setSuccessMessage(responseMessage ?? 'Thank you!')
        }
        setFormSubmitted(true)
    }, [])

    const renderInputs = (inputs) => {
        const renderedInputs = inputs.map((input, index) => {
            if (input.type === 'hidden') {
                return (
                    <input
                        type='hidden'
                        name={input.name}
                        value={input.value}
                        key={index}
                    />
                )
            } else {
                return (
                    <div className='hw-block hw-block--mb-medium-2' key={index}>
                        {renderInputType(input, index)}
                    </div>
                )
            }
        })
        return <>{renderedInputs}</>
    }

    const renderInputType = (input, index) => {
        if (input.type === 'textarea') {
            return (
                <Textarea
                    label={input.label}
                    name={input.name}
                    value={input.value}
                    onChange={(e) => handleChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'text') {
            return (
                <TextInput
                    label={input.label}
                    name={input.name}
                    value={input.value}
                    onChange={(e) => handleChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'email') {
            return (
                <EmailInput
                    id={input.id}
                    label={input.label}
                    name={input.name}
                    value={input.value}
                    onChange={(e) => handleChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'select') {
            return (
                <Dropdown
                    id={input.id}
                    label={input.label}
                    name={input.name}
                    options={input.options}
                    onChange={(e) => handleDropDownChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'radio') {
            return (
                <RadioButtonGroup
                    name={input.name}
                    id={input.id}
                    label={input.label}
                    options={input.options}
                    callBack={(e) => handleRadioChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'checkboxes') {
            return (
                <CheckboxGroup
                    name={input.name}
                    label={input.label}
                    options={input.options}
                    onChange={handleCheckboxChange}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'file') {
            return (
                <FileInput
                    name={input.name}
                    id={input.id}
                    label={input.label}
                    onChange={(e) => handleFileChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'header') {
            return <h3 className='hw-h3'>{input.label}</h3>
        }
    }

    const validate = () => {
        let hasChanges = false
        const newInputList = inputs.map((input) => {
            if (!validateFormInput(input)) {
                input.errorMessage = errorMessages[input.type]
                hasChanges = true
            } else {
                if (input.errorMessage) {
                    delete input.errorMessage
                    hasChanges = true
                }
            }
            return input
        })

        if (hasChanges) {
            setInputs(newInputList)
        }
        return newInputList.filter((input) => input.errorMessage).length === 0 // no error messages
    }

    const handleCheckboxChange = ({ target }) => {
        let updated = false
        let updatedInputs = inputs.map((input) => {
            if (input.name === target.name) {
                let value = ''
                let updatedOptions = input.options.map((option) => {
                    if (option.value === target.value) {
                        option.checked = target.checked
                        updated = true
                    }
                    if (option.checked) {
                        value += option.value + ','
                    }
                    return option
                })
                if (updated) {
                    input.value = value.slice(0, -1)
                    input.options = updatedOptions
                    delete input.errorMessage
                }
            }
            return input
        })
        if (updated) {
            setInputs(updatedInputs)
        }
    }

    const handleRadioChange = ({ target }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === target.name) {
                    let updated = false
                    let updatedOptions = input.options.map((option) => {
                        if (option.value === target.value) {
                            option.checked = true
                            updated = true
                        } else {
                            option.checked = false
                        }
                        return option
                    })
                    if (updated) {
                        input.options = updatedOptions
                        input.value = target.value
                        delete input.errorMessage
                    }
                }
                return input
            })
        )
    }

    const handleFileChange = ({ target: { name, value, files } }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === name) {
                    input.value = value
                    input.file = files[0]
                    delete input.errorMessage
                }
                return input
            })
        )
    }

    const handleDropDownChange = ({ target: { name, value } }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === name) {
                    input.value = value
                    delete input.errorMessage
                }
                return input
            })
        )
    }

    const handleChange = ({ target: { name, value } }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === name) {
                    input.value = value
                    delete input.errorMessage
                }
                return input
            })
        )
    }

    /**
     * Reduce inputs array to object
     * Looks like
     * { email: 'person@company.no', name: 'Person' }
     * TODO: Should ignore subheaders
     */
    const inputsReducer = (obj, { name, value }) => ({ ...obj, [name]: value })

    const preSubmit = (event) => {
        validate()
    }

    const submit = (event) => {
        event.preventDefault()
        if (validate()) {
            if (recaptchaIsConfigured) {
                grecaptcha.ready(() => {
                    grecaptcha
                        .execute(recaptchaSiteKey, { action: 'submit' })
                        .then((token) => {
                            let body = JSON.stringify(
                                inputs.reduce(inputsReducer, { token })
                            )

                            let formData = new FormData()

                            inputs.map((input) => {
                                if (input.type === 'file') {
                                    formData.append(input.name, input.file)
                                } else {
                                    formData.append(input.name, input.value)
                                }
                            })

                            formData.append('token', token)

                            fetch(form.actionUrl, {
                                method: 'POST',
                                body: formData
                            }).then(handleSubmitResponse)
                        })
                })
            } else {
                let formData = new FormData()

                inputs.map((input) => {
                    if (input.type === 'file') {
                        formData.append(input.name, input.file)
                    } else {
                        formData.append(input.name, input.value)
                    }
                })

                fetch(form.actionUrl, {
                    method: 'POST',
                    body: formData
                }).then(handleSubmitResponse)
            }
        }
    }

    return (
        <section>
            {formSubmitted ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: successMessage
                            ? successMessage
                            : serverErrorMessage
                    }}
                ></div>
            ) : (
                <form
                    id={form.id}
                    onSubmit={(event) => submit(event)}
                    method='POST'
                >
                    {renderInputs(inputs)}
                    <PrimaryButton
                        type='submit'
                        onClick={(event) => preSubmit(event)}
                    >
                        {form.submitText}
                    </PrimaryButton>
                </form>
            )}
        </section>
    )
}

export default (props) => <KPForm {...props} />
